import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock } from 'lucide-react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Card, CardContent } from '../../../../components/ui/Card';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import AppIcon from '../../../../components/AppIcon';

interface ReflectionResponse {
  prompt: string;
  answer: string;
}

interface PreviousReflection {
  id: string;
  month: string;
  status: string;
  completedPrompts: number;
  totalPrompts: number;
  responses: ReflectionResponse[];
  managerResponse: string;
}

interface PreviousReflectionsProps {
  reflections: PreviousReflection[];
}

const PreviousReflections: React.FC<PreviousReflectionsProps> = ({ reflections }) => {
  const { t, i18n } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!reflections || reflections.length === 0) {
    return (
      <Card>
        <CardContent>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('performance.reflections.previousReflections')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('performance.reflections.noPreviousReflections')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          {t('performance.reflections.previousReflections')}
        </h3>

        {reflections.map((reflection) => (
          <Collapsible.Root
            key={reflection.id}
            open={expandedId === reflection.id}
            onOpenChange={(open) => setExpandedId(open ? reflection.id : null)}
            className="mb-4"
          >
            <Collapsible.Trigger asChild>
              <button
                type="button"
                className="w-full p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors text-start"
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <AppIcon name="Calendar" size={16} className="text-muted-foreground" />
                    <span className="text-base font-medium text-foreground">
                      {new Date(reflection.month).toLocaleDateString(i18n.language, {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      variant={reflection.status === 'complete' ? 'success' : 'neutral'}
                      icon={reflection.status === 'complete' ? CheckCircle : Clock}
                    >
                      {reflection.status === 'complete'
                        ? t('performance.reflections.complete')
                        : t('performance.reflections.inProgress')}
                    </StatusBadge>
                    <AppIcon
                      name={expandedId === reflection.id ? 'ChevronUp' : 'ChevronDown'}
                      size={16}
                      className="text-muted-foreground"
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('performance.reflections.promptsCompleted', {
                    completed: reflection.completedPrompts,
                    total: reflection.totalPrompts,
                    month: new Date(reflection.month).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
                  })}
                </p>
              </button>
            </Collapsible.Trigger>

            <Collapsible.Content>
              <div className="p-4 border border-border border-t-0 rounded-b-lg">
                {reflection.responses?.length > 0 ? (
                  <>
                    <p className="text-sm font-medium text-foreground mb-2">
                      {t('performance.reflections.yourResponses')}
                    </p>
                    <ul className="space-y-3">
                      {reflection.responses.map((response, index) => (
                        <li key={index}>
                          <p className="text-sm font-medium text-foreground">{response.prompt}</p>
                          <p className="text-sm text-muted-foreground">{response.answer}</p>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('performance.reflections.noResponses')}
                  </p>
                )}

                {reflection.managerResponse && (
                  <>
                    <hr className="my-4 border-border" />
                    <div className="flex items-center gap-2 mb-2">
                      <AppIcon name="MessageCircle" size={16} className="text-primary" />
                      <p className="text-sm font-medium text-foreground">
                        {t('performance.reflections.managerFeedbackLabel')}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">{reflection.managerResponse}</p>
                  </>
                )}
              </div>
            </Collapsible.Content>
          </Collapsible.Root>
        ))}
      </CardContent>
    </Card>
  );
};

export default PreviousReflections;
